import { CheckoutOrderFragment, TravellerFieldEnum, SubsidyTravellerParameters } from '@websky/graphql';
import { getInputType } from './Passengers';
import i18n from 'i18next';
import {
	AdditionalTravellerFieldEnum,
	InputType,
	PassengerField,
	SubsidyField,
	SubsidyOption
} from '../../../../../Passenger/types';
import { SubsidiesInfoByPassengers } from '../../../../store/subsidies/reducers';
import { FormValues } from '../../../../types';

export const getAccompanyingFields = (orderCustomer: CheckoutOrderFragment['customer']) => {
	const customerFields = orderCustomer.values
		.filter(value =>
			[TravellerFieldEnum.AccompanyingPersonName, TravellerFieldEnum.AccompanyingPersonTicketNumber].includes(
				value.type
			)
		)
		.map(({ name, type, validationRules, value, isEditable }) => {
			return {
				name,
				readonly: !isEditable,
				type: type,
				input: getInputType(type),
				placeholder: i18n.t(`Passenger:${name}`),
				validations: validationRules,
				defaultValue: value
			} as PassengerField;
		});

	if (customerFields.length) {
		customerFields.push({
			name: `accompanyingCheck`,
			readonly: false,
			type: AdditionalTravellerFieldEnum.AccompanyingCheck,
			input: InputType.Checkbox,
			placeholder: '',
			validations: [
				{
					required: true,
					with: null,
					hidden: null,
					label: null,
					hint: null,
					placeholder: null,
					minDate: null,
					maxDate: null,
					maxLength: null,
					regExp: null,
					options: null
				}
			],
			defaultValue: null
		});
	}

	return customerFields;
};

export const subsidyFieldFactory = (subsidies: SubsidiesInfoByPassengers[], disabled: boolean): SubsidyField => {
	const options: SubsidyOption[] = subsidies.map(subsidy => {
		// Фильтруем поля, которые уже есть в основной форме пассажира
		const additionalFields = subsidy.validation
			? subsidy.validation
					.filter(
						field =>
							field &&
							![
								TravellerFieldEnum.LastName,
								TravellerFieldEnum.FirstName,
								TravellerFieldEnum.Country,
								TravellerFieldEnum.AccompanyingPersonName,
								TravellerFieldEnum.MiddleName,
								TravellerFieldEnum.AccompanyingPersonTicketNumber,
								TravellerFieldEnum.DocType,
								TravellerFieldEnum.BirthDate,
								TravellerFieldEnum.Citizenship,
								TravellerFieldEnum.Email,
								TravellerFieldEnum.Gender,
								TravellerFieldEnum.KzIinDocument,
								TravellerFieldEnum.LoyaltyAirline,
								TravellerFieldEnum.LoyaltyNumber,
								TravellerFieldEnum.PhoneCountry,
								TravellerFieldEnum.PromoCode,
								TravellerFieldEnum.Phone,
								TravellerFieldEnum.DocNumber,
								TravellerFieldEnum.DocExpiryDate,
								TravellerFieldEnum.Nationality
							].includes(field.type)
					)
					.map(field => ({
						name: field.name,
						readonly: !field.isEditable,
						type: field.type,
						input: getInputType(field.type),
						placeholder: i18n.t(`Passenger:${field.name}_placeholder`, { defaultValue: field.name }),
						validations: field.validationRules || [],
						defaultValue: field.value || '',
						isSubsidyAdditionalField: true
					}))
			: [];

		return {
			key: subsidy.id,
			value: subsidy.id,
			label: subsidy.name,
			type: subsidy.type,
			disabled: disabled,
			additionalFields
		};
	});

	return {
		name: 'subsidy',
		type: AdditionalTravellerFieldEnum.Subsidy,
		input: InputType.Select,
		readonly: false,
		options,
		defaultValue: '',
		placeholder: i18n.t('Passenger:subsidy', { defaultValue: 'Выберите субсидию' }),
		validations: [
			{
				required: false,
				with: null,
				hidden: null,
				label: null,
				hint: null,
				placeholder: null,
				minDate: null,
				maxDate: null,
				maxLength: null,
				regExp: null,
				options: options.map(opt => ({
					label: opt.label,
					value: opt.value
				}))
			}
		]
	};
};

export const subsidiesCheckTravellersFactory = (
	values: FormValues,
	order: CheckoutOrderFragment
): SubsidyTravellerParameters[] => {
	const travellersToCheck: SubsidyTravellerParameters[] = [];

	order.travellers.forEach((orderTraveller, index) => {
		const formPassenger = values.passengers[index];

		if (!formPassenger) {
			return;
		}

		const subsidyId = formPassenger.subsidy as string;

		// Проверяем наличие выбранной субсидии
		if (!subsidyId || subsidyId.trim() === '') {
			return;
		}

		// Находим выбранную субсидию для получения дополнительных полей
		const selectedSubsidy = orderTraveller.subsidyInfo?.subsidies?.find(subsidy => subsidy.id === subsidyId.trim());

		// Собираем значения полей пассажира из основной формы
		const passengerValues = orderTraveller.values
			.map(field => {
				const value = formPassenger[field.name] as string;

				// Фильтруем пустые значения
				if (!value || value.trim() === '') {
					return null;
				}

				return {
					name: field.name,
					type: field.type,
					value: value.trim()
				};
			})
			.filter(Boolean); // Удаляем null значения

		// Добавляем дополнительные поля из validation субсидии
		if (selectedSubsidy?.validation) {
			selectedSubsidy.validation.forEach(validationField => {
				if (!validationField) {
					return;
				}

				const fieldName = validationField.name;
				const fieldValue = formPassenger[fieldName] as string;

				// Проверяем, что поле не пустое и ещё не добавлено
				if (fieldValue && fieldValue.trim() !== '' && !passengerValues.some(v => v.name === fieldName)) {
					passengerValues.push({
						name: fieldName,
						type: validationField.type,
						value: fieldValue.trim()
					});
				}
			});
		}

		travellersToCheck.push({
			id: orderTraveller.id,
			subsidyId: subsidyId.trim(),
			values: passengerValues
		});
	});

	return travellersToCheck;
};
